import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Clock, ShieldCheck, Zap } from "lucide-react";
import { fadeUp, staggerContainer, viewportOnce } from "@/lib/motion";

const MED_IMG =
  "https://images.unsplash.com/photo-1704662451911-198af41df592?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxtZWRpY2luZSUyMGJveGVzJTIwd2hpdGUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4NTUyOTQ3N3ww&ixlib=rb-4.1.0&q=85";

const POINTS = [
  { icon: Clock, label: "No 24–48h waits", color: "var(--urgent-orange)" },
  { icon: ShieldCheck, label: "Verified local chemists", color: "var(--action-green)" },
  { icon: Zap, label: "Real stock, right now", color: "#0f172a" },
];

export const WhyUs = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["12%", "-12%"]);

  return (
    <section
      ref={ref}
      className="relative bg-white py-24 sm:py-32"
      data-testid="why-us-section"
    >
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-14 px-5 sm:px-8 md:grid-cols-2">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          <motion.p
            variants={fadeUp}
            className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400"
          >
            Why us
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl"
          >
            Urgent needs can&apos;t wait{" "}
            <span style={{ color: "var(--urgent-orange)" }}>24–48 hours.</span>
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-6 max-w-lg text-lg leading-relaxed text-slate-600"
          >
            For acute needs like fever, antibiotics, or pain relief, you need it
            today. We connect you directly with nearby chemists who actually have
            your medicine in stock right now.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-3">
            {POINTS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.label}
                  className="flex items-center gap-2 rounded-full border border-slate-100 bg-slate-50 px-4 py-2"
                >
                  <Icon size={16} style={{ color: p.color }} aria-label={p.label} />
                  <span className="text-sm font-medium text-slate-700">
                    {p.label}
                  </span>
                </div>
              );
            })}
          </motion.div>
        </motion.div>

        {/* Clipped product frame */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={viewportOnce}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="relative overflow-hidden rounded-[2rem] border border-slate-100 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)]">
            <motion.img
              style={{ y: imgY }}
              src={MED_IMG}
              alt="Clean medicine boxes"
              className="h-[26rem] w-full scale-110 object-cover"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-black/5" />
          </div>

          <div
            className="absolute -left-5 bottom-8 rounded-2xl bg-white p-4 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.3)]"
            data-testid="why-us-floating-card"
          >
            <p className="font-display text-3xl font-extrabold text-slate-900">
              &lt; 60 min
            </p>
            <p className="text-xs font-medium text-slate-500">
              average delivery window
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyUs;
